"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.stringifyCallSignature = exports.stringify = void 0;
const constants_1 = require("../constants");
const listHelpers_1 = require("./listHelpers");
function makeIndent(indent) {
    if (typeof indent === 'number') {
        if (indent > 10) {
            indent = 10;
        }
        return ' '.repeat(indent);
    }
    else {
        if (indent.length > 10) {
            indent = indent.substring(0, 10);
        }
        return indent;
    }
}
function indentify(indent, s) {
    return s
        .split('\n')
        .map(v => indent + v)
        .join('\n');
}
const stringify = (value, indent = 2, splitlineThreshold = 80) => {
    // Used to check if there are any cyclic structures
    const ancestors = new Set();
    // Precompute useful strings
    const indentString = makeIndent(indent);
    // Util functions
    // Determines if the array/object containing these (stringified) values
    // should be multiline and indented or oneline
    const shouldMultiline = (valueStrs) => indentString !== '' &&
        (valueStrs.join(', ').length > splitlineThreshold || valueStrs.some(s => s.includes('\n')));
    // Stringify functions
    // The real one is stringifyValue
    const stringifyPair = (pair, indentLevel) => {
        const list = listHelpers_1.flattenPairToList(pair);
        if (list.type === 'List') {
            return stringifyList(list.value, indentLevel);
        }
        else {
            return stringifyImproperList(list.value, indentLevel);
        }
    };
    const stringifyList = (list, indentLevel) => {
        const prefix = '(';
        const suffix = ')';
        const prefixIndented = prefix + indentString.substring(prefix.length);
        const suffixIndented = suffix;
        const valueStrs = list.map(x => {
            ancestors.add(x.pair);
            const value = stringifyValue(x.value, 0);
            return value;
        });
        list.forEach(x => ancestors.delete(x.pair));
        if (shouldMultiline(valueStrs)) {
            // indent second element onwards to match with first element
            return `${prefixIndented}${indentify(indentString.repeat(indentLevel) + ' '.repeat(prefixIndented.length), valueStrs.join(' \n')).substring(prefixIndented.length)}${suffixIndented}`;
        }
        else {
            return `${prefix}${valueStrs.join(' ')}${suffix}`;
        }
    };
    const stringifyImproperList = (list, indentLevel) => {
        const prefix = '(';
        const suffix = ')';
        const prefixIndented = prefix + indentString.substring(prefix.length);
        const suffixIndented = suffix;
        const preStrs = list.properPart.map(x => {
            ancestors.add(x.pair);
            return stringifyValue(x.value, 0);
        });
        ancestors.add(list.lastPair);
        const postStrs = [stringifyValue(list.lastPair.head), '.', stringifyValue(list.lastPair.tail)];
        const valueStrs = preStrs.concat(postStrs);
        list.properPart.forEach(x => ancestors.delete(x.pair));
        ancestors.delete(list.lastPair);
        if (shouldMultiline(valueStrs)) {
            // indent second element onwards to match with first element
            return `${prefixIndented}${indentify(indentString.repeat(indentLevel) + ' '.repeat(prefixIndented.length), valueStrs.join(' \n')).substring(prefixIndented.length)}${suffixIndented}`;
        }
        else {
            return `${prefix}${valueStrs.join(' ')}${suffix}`;
        }
    };
    const stringifyValue = (v, indentLevel = 0) => {
        if (ancestors.has(v)) {
            return '...<circular>';
        }
        else if (ancestors.size > constants_1.MAX_LIST_DISPLAY_LENGTH) {
            return '...<truncated>';
        }
        switch (v.type) {
            case 'boolean':
                return v.value ? '#t' : '#f';
            case 'number':
                return `${v.value}`;
            case 'empty list':
                return '()';
            case 'string':
                return `"${v.value}"`;
            case 'symbol':
                return v.value;
            case 'pair':
                return stringifyPair(v, indentLevel);
            case 'procedure':
                const procedureVariant = v.variant === 'CompoundProcedure' ? 'compound procedure' : 'built-in procedure';
                return `[${procedureVariant} ${exports.stringifyCallSignature(v.name, v.callSignature)}]`;
            case 'macro':
                return `[macro '${v.name}']`;
            case 'TailCall':
                throw new Error('stringify should not see a TailCall value');
        }
    };
    return stringifyValue(value, 0);
};
exports.stringify = stringify;
const stringifyCallSignature = (name, callSignature) => {
    let argNames = [];
    if (callSignature.style === 'fixed-args') {
        if ('parameters' in callSignature) {
            argNames = callSignature.parameters.map(param => param.value);
        }
        else {
            argNames = Array.from({ length: callSignature.numParams }).map((_, index) => `arg${index}`);
        }
    }
    else {
        if ('compulsoryParameters' in callSignature) {
            argNames = callSignature.compulsoryParameters
                .map(param => param.value)
                .concat(['...' + callSignature.restParameters.value]);
        }
        else {
            argNames = Array.from({ length: callSignature.numCompulsoryParameters })
                .map((_, index) => `arg${index}`)
                .concat(['...rest-args']);
        }
    }
    return `(${[name, ...argNames].join(' ')})`;
};
exports.stringifyCallSignature = stringifyCallSignature;
//# sourceMappingURL=stringify.js.map